'use client';

import { InvitationAlert } from '@/app/(auth)/invitations/components/invitation-alert';
import { registerAction } from '@/app/lib/actions/auth';
import { SlideUp } from '@/components/slide-up';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { registerSchema, type RegisterInput } from '@/schemas/auth';
import { Invitation } from '@/types/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';

interface RegisterFormProps {
  invitationCode?: string;
  invitationData?: Invitation;
}

export function RegisterForm({ invitationCode, invitationData }: RegisterFormProps = {}) {
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const searchParams = useSearchParams();
  const accountType = searchParams.get('type') || 'individual';

  const form = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema(accountType === 'organization')),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirmation: '',
      dob: '',
      title: '',
      type: 0,
      organization_name: '',
      organization_seats: 5,
      organization_invoice_period: '6',
    },
    mode: 'onBlur',
  });

  // Set email from invitation data when component mounts
  useEffect(() => {
    if (invitationData?.email) {
      form.setValue('email', invitationData.email);
    }
  }, [form, invitationData]);

  async function onSubmit(data: RegisterInput) {
    startTransition(async () => {
      setShowDialog(false);
      const formData = new FormData();
      const personalInfoFields = [
        'title',
        'dob',
        'first_name',
        'last_name',
        'email',
        'password',
        'password_confirmation',
        'type',
      ];

      personalInfoFields.forEach((field) => {
        const value = data[field as keyof RegisterInput] as string;

        if (field === 'dob' && (!value || value.trim() === '')) {
          return;
        }

        formData.append(field, value);
      });

      if (accountType === 'organization') {
        formData.append('organization_name', data.organization_name || '');
        formData.append('organization_seats', data.organization_seats?.toString() || '5');
        formData.append('organization_invoice_period', data.organization_invoice_period || '6');
      }

      // If invitation code is provided, add it to the form data
      if (invitationCode) {
        formData.append('invitation_token', invitationCode);
      }

      const result = await registerAction(formData);

      if (result.needVerification) {
        setDialogMessage(result.message || 'Verification email has been sent to your email');
        setShowDialog(true);
        return;
      }

      if (result.validation) {
        Object.entries(result.validation).forEach(([field, errors]) => {
          form.setError(field as keyof RegisterInput, {
            message: (errors as string[])[0],
          });
        });
      } else if (!result.success && result.error) {
        form.setError('root', { message: result.error });
      }
    });
  }

  return (
    <SlideUp>
      <Form {...form}>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Verification Required</DialogTitle>
              <DialogDescription>{dialogMessage}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" asChild>
                <Link href="/login">Go to Login</Link>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {form.formState.errors.root && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {form.formState.errors.root.message}
            </div>
          )}

          {invitationData && <InvitationAlert invitationCode={invitationCode} invitationData={invitationData} />}
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger className="w-[78px]">
                      <SelectValue placeholder="Select a title" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr">Mr</SelectItem>
                      <SelectItem value="Mrs">Mrs</SelectItem>
                      <SelectItem value="Ms">Ms</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    First Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Last Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Email <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="email" {...field} disabled={!!invitationCode && !!invitationData} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Password <span className="text-destructive">*</span>
                  </FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input type={showPassword ? 'text' : 'password'} className="pr-10" {...field} />
                    </FormControl>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password_confirmation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Confirm Password <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type={showPassword ? 'text' : 'password'} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="dob"
            render={({ field }) => (
              <FormItem className="w-1/3">
                <FormLabel>Date of Birth</FormLabel>
                <FormControl>
                  <Input type="date" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {accountType === 'organization' && (
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="organization_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Organization Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="organization_seats"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Number of Seats <span className="text-destructive">*</span>{' '}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={5}
                          {...field}
                          value={field.value}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="organization_invoice_period"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Time Period <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select time period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="6">6 Months</SelectItem>
                            <SelectItem value="12">1 Year</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              By signing up I agree to the{' '}
              <span className="text-primary">
                <Link href="terms-and-privacy">Terms & Privacy</Link>
              </span>
              .
            </p>
          </div>
        </form>
        <p className="mt-6 text-center text-[#4b4b4b]">
          Already have an account?{' '}
          <Link href="/login" className="text-primary hover:underline">
            Login
          </Link>
        </p>
      </Form>
    </SlideUp>
  );
}
